import { SelectItem } from '@wui/common-library'
import { computed, type MaybeRefOrGetter, toValue } from 'vue'

import { useSearchContactsByStringPaginated } from '@/entities/contact'

import { useCurrentUser } from '@/shared/composables/current-user'
import { Contact } from '@/shared/types/api'
import { Room } from '@/shared/types/store'
import { searchContact } from '@/shared/utils/utils'

import { guestToContact } from './guest-to-contact'

const dedupeSelectItems = (items: SelectItem<Contact>[]): SelectItem<Contact>[] => {
  const seen = new Set<string>()

  return items.filter((item) => {
    if (seen.has(item.value.id)) {
      return false
    }

    seen.add(item.value.id)
    return true
  })
}

const filterBySearch = (items: SelectItem<Contact>[], searchValue: string): SelectItem<Contact>[] => {
  if (!searchValue) {
    return items
  }

  const isDigitsOnly = /^\d+$/.test(searchValue)

  return items.filter(item => searchContact(item.value, searchValue, isDigitsOnly))
}

export const useScheduleParticipantsSelect = (room?: MaybeRefOrGetter<Room | undefined>) => {
  const { currentUser } = useCurrentUser()
  const {
    string,
    fetchNext,
    items: searchedItems,
    allItems: addressBookAllItems,
  } = useSearchContactsByStringPaginated()

  /** Чтобы после удаления чипса «Вы» организатора можно было выбрать снова. */
  const currentUserSelectItem = computed((): SelectItem<Contact> | null => {
    const user = currentUser.value
    if (!user) {
      return null
    }

    const contact = user as Contact
    return {
      label: contact.name,
      value: contact,
    }
  })

  const roomSelectItems = computed((): SelectItem<Contact>[] => {
    const currentRoom = toValue(room)
    if (!currentRoom) {
      return []
    }

    const items: SelectItem<Contact>[] = []

    for (const [, member] of currentRoom.subscribers ?? []) {
      if (member.imLogin === currentUser.value?.imLogin) {
        continue
      }

      items.push({
        label: member.name,
        value: member,
      })
    }

    for (const [, guest] of currentRoom.guests ?? []) {
      const contact = guestToContact(guest)

      items.push({
        label: contact.name,
        value: contact,
      })
    }

    return items
  })

  const withCurrentUser = (
    list: SelectItem<Contact>[],
    searchValue = '',
  ): SelectItem<Contact>[] => {
    const self = currentUserSelectItem.value
    if (!self) {
      return list
    }
    if (searchValue && filterBySearch([self], searchValue).length === 0) {
      return list
    }
    return dedupeSelectItems([self, ...list])
  }

  const items = computed(() => {
    if (string.value) {
      return withCurrentUser([
        ...filterBySearch(roomSelectItems.value, string.value),
        ...searchedItems.value,
      ], string.value)
    }

    return withCurrentUser([
      ...roomSelectItems.value,
      ...addressBookAllItems.value,
    ])
  })

  const allItems = computed(() => withCurrentUser([
    ...roomSelectItems.value,
    ...addressBookAllItems.value,
    ...(string.value ? searchedItems.value : []),
  ]))

  return {
    string,
    fetchNext,
    items,
    allItems,
  }
}
